import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { PsychologicalPriceStrategy } from '@/lib/graphql/generated/graphql';
import { useComputeChoosePrice } from '@/lib/hooks/useComputeChoosePrice';
import { useExecuteOptimisticUpdate } from '@/lib/hooks/useExecuteOptimisticUpdate';

interface ApplyPsychoPricesArgs {
  itemIds: string[];
  strategy: PsychologicalPriceStrategy;
  errorContext: string;
  onSuccess?: () => void;
  onSuccessClose?: () => void;
}

/**
 * Recomputes "choose" (psychological) prices for the given items through the
 * compute-choose-price mutation, with the shared success toast, in-progress
 * message and error handling. Used by both the bulk-edit floating menu and the
 * anomalies sidebar so the flow stays in sync.
 */
export const useApplyPsychoPrices = (dealId: string) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { computeChoosePrice } = useComputeChoosePrice(dealId);
  const { executeOptimisticUpdate } = useExecuteOptimisticUpdate();

  const applyPsychoPrices = useCallback(
    async ({
      itemIds,
      strategy,
      errorContext,
      onSuccess,
      onSuccessClose,
    }: ApplyPsychoPricesArgs): Promise<boolean> => {
      if (itemIds.length === 0) return false;

      return await executeOptimisticUpdate({
        itemIds,
        optimisticUpdate: (prev) => prev,
        mutation: () => computeChoosePrice(itemIds, strategy),
        modifiedFields: ['choosePrice'],
        checkSuccess: (result) =>
          result.data?.computeChoosePriceV2?.__typename ===
          'MutationUpdateDealItemsSuccess',
        getErrorMessage: (result) =>
          result.data?.computeChoosePriceV2?.__typename === 'DomainException'
            ? result.data.computeChoosePriceV2.code
            : t('components.bulk_edit_sidebar.apply_psycho_prices_error'),
        defaultErrorMessage: t(
          'components.bulk_edit_sidebar.apply_psycho_prices_error'
        ),
        errorContext,
        inProgressMessage: t('general.psycho_prices_in_progress'),
        onSuccess: () => {
          toast({
            title: t('general.success'),
            description: t(
              'components.bulk_edit_sidebar.apply_psycho_prices_success_toast',
              { count: itemIds.length }
            ),
          });
          onSuccess?.();
        },
        onSuccessClose,
      });
    },
    [computeChoosePrice, executeOptimisticUpdate, t, toast]
  );

  return { applyPsychoPrices };
};
